import { SEVERITY_RANK } from '@/features/document/domain/severity';
import { IssueSeverity } from '@/shared/api/generated-types';

import type { RowErrors, CtuBreakdownEntry } from '@/features/document/domain/errorTypes';

type BreakdownField = {
  field: string;
  label: string;
  severity: IssueSeverity;
  message?: string | null;
};

function fieldsToRow(fields: readonly BreakdownField[] | undefined): RowErrors {
  const row: RowErrors = {};
  for (const f of fields ?? []) {
    const existing = row[f.field];
    if (!existing || SEVERITY_RANK[f.severity] < SEVERITY_RANK[existing.severity]) {
      row[f.field] = { label: f.label, message: f.message ?? '', severity: f.severity };
    }
  }
  return row;
}

export function deriveErrorMapsFromBreakdown(
  breakdown: readonly CtuBreakdownEntry[] | null | undefined
): { cargoErrors: Map<string, RowErrors>; ctuErrors: Map<string, RowErrors> } {
  const cargoErrors = new Map<string, RowErrors>();
  const ctuErrors = new Map<string, RowErrors>();

  for (const entry of breakdown ?? []) {
    if (entry.ctuFields?.length) {
      ctuErrors.set(entry.ctuId, fieldsToRow(entry.ctuFields));
    }
    for (const item of entry.items ?? []) {
      cargoErrors.set(item.cargoId, fieldsToRow(item.fields));
    }
  }

  return { cargoErrors, ctuErrors };
}

export function parseCtuIndexFromProperty(property: string | undefined): number | null {
  if (!property) return null;
  const match = property.match(/CargoTransportUnits\[(\d+)\]/);
  return match ? parseInt(match[1], 10) : null;
}

export function backendPropertyToFieldPath(property: string): string {
  return property
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .map((seg) => seg.charAt(0).toLowerCase() + seg.slice(1))
    .join('.');
}

function rowsEqual(a: RowErrors, b: RowErrors): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => {
    const x = a[k];
    const y = b[k];
    return !!y && x.label === y.label && x.message === y.message && x.severity === y.severity;
  });
}

export function reuseUnchangedRows(
  next: Map<string, RowErrors>,
  prev: Map<string, RowErrors>
): Map<string, RowErrors> {
  let changed = next.size !== prev.size;
  for (const [id, row] of next) {
    const old = prev.get(id);
    if (old && rowsEqual(old, row)) next.set(id, old);
    else changed = true;
  }
  return changed ? next : prev;
}
